import { useCallback, useRef, useState } from 'react';
import { Spinner } from 'components/elements/Spinner';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import ProjectInfo from 'components/widgets/ProjectDetails/ProjectInfo';
import ProjectDetailsWithAISection from 'components/widgets/ProjectDetails/sections/ProjectDetailsWithAISection';
import ProjectActivitySection from 'pages/ProjectDetails/ActivitySection';
import LandTitleSection from 'pages/ProjectDetails/LandTitleSection';
import PastProjectsSection from 'pages/ProjectDetails/PastProjectsSection';
import ProjectsInParcelSection from 'pages/ProjectDetails/ProjectsInParcelSection';
import { Project } from 'types';
import { LandTitle } from 'types/LandTitle';
import { DocumentOverview } from 'types/TempDocument';

const pdpSectionStyle = {
  marginTop: (theme: any) => theme.space.thirtyTwo,
  width: '100%',
};

type ProjectDetailsBodyProps = {
  project: Project | DocumentOverview | null | undefined;
  isLoadingProject: boolean;
  projectActivitySummary: any;
  isLoadingProjectActivitySummary: boolean;
  isErrorLoadingActivitySummary: boolean;
  projectSizeInfo: any;
  projectWorkType?: string[] | undefined;
  landTitleHistory: LandTitle[] | undefined;
  isLoadingLandTitleHistory: boolean;
  isProjectsByAddressLoading: boolean;
  projectsList: any;
  numberOfProjectsByAddress: number | undefined;
  setPage: (page: number) => void;
  showAISection?: boolean;
  projectEvents?: any;
  isLoadingProjectEvents?: boolean;
};

const ProjectDetailsBody = ({
  project,
  isLoadingProject,
  projectActivitySummary,
  isLoadingProjectActivitySummary,
  isErrorLoadingActivitySummary,
  projectSizeInfo,
  projectWorkType,
  landTitleHistory,
  isLoadingLandTitleHistory,
  isProjectsByAddressLoading,
  projectsList,
  numberOfProjectsByAddress,
  setPage,
  showAISection = true,
  projectEvents,
  isLoadingProjectEvents,
}: ProjectDetailsBodyProps) => {
  const [tabIndex, setTabIndex] = useState(0);

  const landTitleSectionRef = useRef<HTMLDivElement>(null);

  const handleTabClick = useCallback(
    (index: number) => {
      setTabIndex(index);
      if (landTitleSectionRef.current) {
        landTitleSectionRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [setTabIndex, landTitleSectionRef],
  );

  if (!isLoadingProject && project === undefined) {
    return null;
  }

  if (!project) {
    return <Spinner />;
  }

  return (
    <FlexColumn>
      <ProjectInfo
        identifier={project?.id || ''}
        landTitleHistory={landTitleHistory}
        handleTabClick={handleTabClick}
        project={project}
        isLoading={isLoadingProject}
        projectSizeInfo={projectSizeInfo}
        projectWorkType={projectWorkType}
      />
      {showAISection && (
        <FlexRow sx={pdpSectionStyle}>
          <ProjectDetailsWithAISection
            projectSlug={project.slug || ''}
            documentId={project.slug ? '' : `${project.id}`}
            existingAiInsights={project.ai_insights}
          />
        </FlexRow>
      )}
      <FlexRow sx={pdpSectionStyle}>
        <ProjectActivitySection
          projectCity={project.city || ''}
          projectSlug={project.slug}
          isLoading={isLoadingProjectActivitySummary}
          projectActivitySummary={projectActivitySummary}
          isError={isErrorLoadingActivitySummary}
          projectEvents={projectEvents}
          isLoadingProjectEvents={isLoadingProjectEvents}
        />
      </FlexRow>
      <FlexRow sx={pdpSectionStyle}>
        <LandTitleSection
          landTitleHistory={landTitleHistory}
          landTitlesLoading={isLoadingLandTitleHistory}
          tabIndex={tabIndex}
          setTabIndex={setTabIndex}
          ref={landTitleSectionRef}
        />
      </FlexRow>
      <FlexRow sx={pdpSectionStyle}>
        <PastProjectsSection
          projectSlug={project.slug || ''}
          projectAddress={project?.address || ''}
          isLoading={isProjectsByAddressLoading}
          projectsList={projectsList}
          count={numberOfProjectsByAddress || 0}
          setPage={setPage}
        />
      </FlexRow>
      <FlexRow sx={pdpSectionStyle}>
        <ProjectsInParcelSection
          identifier={project.slug || project?.id || ''}
          projectSlug={project.slug || ''}
          projectAddress={project?.address || ''}
          projectParcelPolygon={project?.parcel_polygon || undefined}
          isLoading={isLoadingProject}
        />
      </FlexRow>
    </FlexColumn>
  );
};

export default ProjectDetailsBody;
